import FormProvider from '@/components/forms/hook-form';
import { useRouter } from '@/routes/hooks';
import { paths } from '@/routes/paths';
import { OrganizationInfoCard } from '@components/data-display/organization';
import { LoadingButton } from '@mui/lab';
import { Button, Grid } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateBillingOrder } from '../../hooks/useCreateBillingOrder';
import { useBillingStore } from '../../store/billing.store';
import {
  buildDistribuitionListData,
  buildThirdStepDistribuitionListDataFromSalesOrders,
  groupUniqueItems,
  groupUniqueItemsFromSalesOrders,
} from '../../utils/billing.utils';
import { BillingDistributionTabs } from '../table/billing-distribution-tab';
import { BillingItemsTable } from '../table/billing-items-table';

interface BillingOrderNewEditThirdstepStepFormProps {
  onBack: () => void;
  onCancel: () => void;
}

export const BillingOrderNewEditThirdstepStepForm = ({
  onBack,
  onCancel,
}: BillingOrderNewEditThirdstepStepFormProps) => {
  const organization = useBillingStore((state) => state.organization);
  const selectedMilestones = useBillingStore((state) => state.selectedMilestones);
  const legalEntityDistributions = useBillingStore((state) => state.legalEntityDistributions);
  const billingOrder = useBillingStore((state) => state.billingOrder);
  const secondStepFormValues = useBillingStore((state) => state.secondStepFormValues);
  const clearStore = useBillingStore((state) => state.clearStore);
  const { t } = useTranslation();
  const methods = useForm();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate, isLoading, reset } = useCreateBillingOrder();

  const onSubmit = methods.handleSubmit(async () => {
    try {
      if (!billingOrder) {
        throw new Error(t('Billing order not found'));
      }
      mutate(billingOrder);
      clearStore();
      reset();
      enqueueSnackbar(t('Order sent!'));
      router.push(paths.management.accounts.billingOrders.root);
    } catch (e) {}
  });

  const groupedUniqueItems = useMemo(() => {
    if (billingOrder?.salesOrder && billingOrder.salesOrder.length > 0) {
      return groupUniqueItemsFromSalesOrders(billingOrder.salesOrder, selectedMilestones);
    }
    return groupUniqueItems(selectedMilestones);
  }, [selectedMilestones, billingOrder?.salesOrder]);
  const distribuitionListItem = useMemo(() => {
    if (!billingOrder?.salesOrder || billingOrder.salesOrder.length === 0) {
      return buildDistribuitionListData(legalEntityDistributions);
    }
    return buildThirdStepDistribuitionListDataFromSalesOrders(
      billingOrder.salesOrder,
      legalEntityDistributions,
      selectedMilestones,
      secondStepFormValues
    );
  }, [
    legalEntityDistributions,
    billingOrder?.salesOrder,
    selectedMilestones,
    secondStepFormValues,
  ]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <OrganizationInfoCard organizationData={organization} contacts={[]} />
      <BillingItemsTable items={groupedUniqueItems} />
      <BillingDistributionTabs distributionListItem={distribuitionListItem} />

      {/* Action Button Section */}
      <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
        <Grid item xs={4} sx={{ textAlign: 'left' }}>
          <LoadingButton
            color="primary"
            type="submit"
            onClick={onBack}
            variant="contained"
            sx={{ mr: 1 }}
          >
            {t('Back')}
          </LoadingButton>
          <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={() => {
              onCancel();
            }}
          >
            {t('Cancel')}
          </Button>
        </Grid>
        <Grid item xs={8} sx={{ textAlign: 'right' }}>
          <LoadingButton
            color="primary"
            onSubmit={onSubmit}
            type="submit"
            variant="contained"
            loading={isLoading}
          >
            {t('Send')}
          </LoadingButton>
        </Grid>
      </Grid>
    </FormProvider>
  );
};
